import { type FC, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { Alert } from '@/components/alert';
import { ButtonAnt } from '@/components/buttons/button/ButtonAnt';
import { Form, OneTimePasswordInput } from '@/components/form';
import { FormattedMessage, useIntl } from '@/components/formatted_message/FormattedMessage';
import { Flex } from '@/components/layouting';
import { Link } from '@/components/link';
import { Typography } from '@/components/typography';
import { useAuthContext } from '@/contexts/AuthContext';
import { StyledAuthLayout } from '@/routes/auth/components/Containers';
import { EnmonLogo } from '@/routes/auth/components/EnmonLogo';
import { OTP_VALIDITY_DURATION } from './constants';
import { Page as messages } from './messages';

const OTP_FORM_ID = 'otp-form';

const { Headline, Paragraph } = Typography;

interface OnetimePasswordValues {
  token: string;
  email: string;
}

export const OneTimePasswordPage: FC = () => {
  const intl = useIntl();
  const [form] = Form.useForm<OnetimePasswordValues>();
  const history = useHistory();

  const {
    identifiedUser: { identifier: email },
    generateOtp,
    verifyOtp,
    loading: { isLoadingGenerateOtp, isLoadingVerifyOtp },
    error: { generateOtp: generateOtpError, verifyOtp: verifyOtpError },
  } = useAuthContext();

  const handleGenerateOtp = useCallback(
    (now: number) => {
      generateOtp({ email });
      localStorage.setItem('lastOtpGeneratedAt', now.toString());
    },
    [email, generateOtp],
  );

  useEffect(() => {
    const lastOtpGeneratedAt = localStorage.getItem('lastOtpGeneratedAt');
    const now = Date.now();

    // Only auto-generate if no previous generation or 3 minutes have passed
    if (!lastOtpGeneratedAt || now - parseInt(lastOtpGeneratedAt, 10) > OTP_VALIDITY_DURATION) {
      handleGenerateOtp(now);
    }
  }, [handleGenerateOtp]);

  return (
    <StyledAuthLayout>
      <Helmet>
        <title>{intl.formatMessage(messages.otpVerification)}</title>
      </Helmet>
      <EnmonLogo />
      <Flex gap={24} vertical>
        <Headline>
          <FormattedMessage {...messages.title} />
        </Headline>

        <Paragraph data-cy="oneTimePassword-description">
          <FormattedMessage values={{ email }} {...messages.description} />
        </Paragraph>

        <Form id={OTP_FORM_ID} form={form} layout="vertical" onFinish={values => verifyOtp(values)}>
          <Form.Item hidden initialValue={email} name="email" />

          <Form.Item colon={false} data-cy="oneTimePassword-token" name="token" rules={[{ required: true }]}>
            <OneTimePasswordInput autoFocus />
          </Form.Item>
        </Form>

        {Boolean(generateOtpError || verifyOtpError) && (
          <Alert description={<FormattedMessage {...messages.verificationFailed} />} showIcon type="error" />
        )}

        <Flex gap={8} vertical>
          <ButtonAnt
            block
            data-cy="oneTimePassword-submit-button"
            disabled={isLoadingGenerateOtp}
            form={OTP_FORM_ID}
            htmlType="submit"
            loading={isLoadingVerifyOtp}
            size="large"
            type="primary"
          >
            <FormattedMessage {...messages.next} />
          </ButtonAnt>
          <ButtonAnt
            block
            data-cy="oneTimePassword-cancel-button"
            disabled={isLoadingVerifyOtp}
            onClick={() => history.push('/login')}
            size="large"
          >
            <FormattedMessage {...messages.cancel} />
          </ButtonAnt>
        </Flex>

        <Flex align="center" gap={8} justify="left">
          <Paragraph data-cy="oneTimePassword-email-not-received" fitContent>
            <FormattedMessage {...messages.emailNotReceived} />
          </Paragraph>
          <Link
            data-cy="oneTimePassword-send-again-link"
            disabled={isLoadingVerifyOtp}
            onClick={() => handleGenerateOtp(Date.now())}
            to="#"
          >
            <FormattedMessage {...messages.sendAgain} />
          </Link>
        </Flex>
      </Flex>
    </StyledAuthLayout>
  );
};
